import React, { useState } from 'react';
import DangerButton from './DangerButton';
import DialogModal from './DialogModal';
import SecondaryButton from './SecondaryButton';
import request from '../utils/request';
import SelectDropdown from './SelectDropdown';

// Backend `assignOrder` now rejects drivers that fail driverEligibility()
// (photo ID / selfie / signed standards / rating / suspension) with a 400
// unless the admin passes `?force=true`. Surface that reason and let the
// admin explicitly override instead of failing silently.
const isEligibilityError = (error) =>
    error?.response?.status === 400 &&
    /not eligible/i.test(error?.response?.data?.error || '');

const getErrorMessage = (error) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Could not assign driver.';

export default function AssignDriver({
    entry,
    setMessage,
    driverData,
    setError,
    // Optional: used by Dispatch to re-point an order that already has a
    // concierge. `excludeDriverId` hides the current concierge from the list.
    label = 'Assign Driver',
    excludeDriverId = null,
    onSuccess = null,
}) {

    const [open, setOpen] = useState(false);
    const [driver, setDriver] = useState();
    const [submitting, setSubmitting] = useState(false);
    const [assignError, setAssignError] = useState('');
    const [canForce, setCanForce] = useState(false);

    const closeModal = () => {
        setOpen(false);
        setAssignError('');
        setCanForce(false);
    };

    const assign = async ({ force = false } = {}) => {
        if (!driver?.value || submitting) {
            return
        }

        setSubmitting(true);
        setAssignError('');
        try {
            const url = `orders/assign-driver/${entry._id}/${driver.value}${force ? '?force=true' : ''}`;
            await request.patch(url);
            closeModal();
            setDriver(undefined);
            // navigate(0); // same as window.location.reload(), but preferred in router context
            setMessage(force ? 'Driver has been assigned (eligibility override).' : 'Driver has been assigned.');
            if (typeof onSuccess === 'function') onSuccess();
        } catch (error) {
            const message = getErrorMessage(error);
            setAssignError(message);
            setCanForce(isEligibilityError(error));
            setError(message)
            console.log(error);
        } finally {
            setSubmitting(false);
        }
    }

    const handleSubmit = () => assign();
    const handleForceSubmit = () => assign({ force: true });

    const driverOptions = driverData && driverData
        .filter((driver) => !excludeDriverId || String(driver.id) !== String(excludeDriverId))
        .map((driver) => {
            return {
                label: driver.name,
                value: driver.id
            }
        })

    return (
        <div>
            <div 
                className='mr-2 px-2 md:px-4 h-[30px] cursor-pointer flex items-center justify-center border border-gray-400 rounded-md whitespace-nowrap overflow-hidden text-ellipsis' 
                onClick={() => setOpen(true)}
            >
                {label}
            </div>
            <DialogModal openModal={open} setOpenModal={(next) => (next ? setOpen(true) : closeModal())}>
                <DialogModal.Content title={label}>
                    <form className="flex justify-between rounded" onSubmit={(e) => e.preventDefault()}>
                        <div className='w-full'>
                            <div className="mb-4 ml-2">
                                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="delivery_charges">
                                    Select Driver
                                </label>
                                <SelectDropdown
                                    value={driver}
                                    options={driverOptions}
                                    isMulti={false}
                                    onChange={option => { setDriver(option); setAssignError(''); setCanForce(false); }}
                                />
                                {assignError && (
                                    <div className="mt-3 text-sm text-red-600">
                                        <p>{assignError}</p>
                                        {canForce && (
                                            <p className="mt-1 text-gray-600">
                                                You can still assign this driver as an admin override. The driver will be notified as usual.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>
                </DialogModal.Content>
                <DialogModal.Footer>
                    {canForce ? (
                        <SecondaryButton
                            type="button"
                            onClick={handleForceSubmit}
                            disabled={submitting}
                            className="!w-auto whitespace-nowrap disabled:opacity-50"
                        >
                            {submitting ? 'Assigning…' : 'Assign anyway'}
                        </SecondaryButton>
                    ) : (
                        <SecondaryButton
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="disabled:opacity-50"
                        >
                            Save
                        </SecondaryButton>
                    )}
                    <DangerButton
                        onClick={closeModal}
                    >
                        Cancel
                    </DangerButton>
                </DialogModal.Footer>
            </DialogModal>
        </div>
    );
}
